import React, { useState, useEffect, useMemo, useRef } from "react";
import { directorioData } from "./directorioData";
import "./DirectorioModal.css";

/**
 * Normaliza cadenas para búsquedas insensibles a mayúsculas y acentos.
 */
function normalizeText(text = "") {
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function DirectorioModal({ isOpen, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUnidad, setSelectedUnidad] = useState("");
  const [contacts, setContacts] = useState(directorioData);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const searchInputRef = useRef(null);

  // Sincronizar contactos desde la API al abrir el modal
  useEffect(() => {
    if (!isOpen) return;

    const fetchDirectorio = async () => {
      const endpoints = [
        "https://sigec.test/api/directorio-general",
        "http://sigec.test/api/directorio-general",
        "/api/directorio-general"
      ];

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint);
          if (!res.ok) continue;
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setContacts(data);
            return;
          }
        } catch {
          // Intentar con siguiente endpoint
        }
      }
      console.warn("Directorio: usando datos locales de respaldo");
    };

    fetchDirectorio();
  }, [isOpen]);

  // Solicitar generación del PDF al backend y abrirlo en una nueva pestaña sin bloqueo de popup
  const handleGenerarYAbrirPDF = async () => {
    if (isGeneratingPDF) return;

    // 1. Abrir la ventana inmediatamente antes de cualquier llamada asíncrona para no perder el gesto del usuario
    const nuevaVentana = window.open("", "_blank");

    // 2. Manejo en caso de bloqueo estricto de ventanas emergentes a nivel navegador
    if (!nuevaVentana) {
      alert(
        "Por favor permita las ventanas emergentes (pop-ups) en su navegador para visualizar el Directorio PDF."
      );
      return;
    }

    // 3. Feedback visual inmediato dentro de la nueva pestaña mientras el backend genera el documento
    try {
      nuevaVentana.document.write(
        "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Generando Directorio PDF...</title><style>body{font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f9fa;color:#333;text-align:center}.box{padding:2.5rem;border-radius:12px;background:#fff;box-shadow:0 4px 14px rgba(0,0,0,0.08);max-width:400px}h2{margin:0 0 10px;color:#5a2864;font-size:1.25rem}p{margin:0;color:#666;font-size:0.95rem;line-height:1.4}</style></head><body><div class='box'><h2>Instituto Tlaxcalteca de Elecciones</h2><p>Generando el Directorio General en PDF, un momento por favor...</p></div></body></html>"
      );
    } catch {
      // Ignorar si el navegador restringe document.write
    }

    try {
      setIsGeneratingPDF(true);

      const endpoints = [
        "https://sigec.test/api/directorio-general/generar-pdf",
        "http://sigec.test/api/directorio-general/generar-pdf",
        "/api/directorio-general/generar-pdf"
      ];

      let responseData = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Accept": "application/json"
            }
          });

          if (res.ok) {
            responseData = await res.json();
            if (responseData && responseData.success && responseData.url) {
              break;
            }
          }
        } catch (e) {
          lastError = e;
        }
      }

      if (responseData && responseData.success && responseData.url) {
        // 6. Redirigir la ventana que ya se encuentra abierta al documento generado
        nuevaVentana.location.href = responseData.url;
      } else {
        // 7. Si falla, cerrar la pestaña temporal y mostrar aviso
        if (nuevaVentana && !nuevaVentana.closed) {
          nuevaVentana.close();
        }
        const errorMsg =
          (responseData && responseData.message) ||
          "No fue posible conectar con el servidor para generar el PDF.";
        console.error("Error al generar PDF del directorio:", errorMsg, lastError);
        alert(`Ocurrió un error al generar el Directorio PDF: ${errorMsg}`);
      }
    } catch (err) {
      if (nuevaVentana && !nuevaVentana.closed) {
        nuevaVentana.close();
      }
      console.error("Error inesperado al solicitar el PDF del directorio:", err);
      alert("Hubo un error de comunicación al solicitar la generación del PDF.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Lista ordenada de Unidades Administrativas
  const unidadesList = useMemo(() => {
    const units = Array.from(new Set(contacts.map((item) => item.unidad).filter(Boolean)));
    return units.sort((a, b) => a.localeCompare(b, "es"));
  }, [contacts]);

  // Filtrado de registros
  const filteredData = useMemo(() => {
    const normalizedQuery = normalizeText(searchTerm);

    return contacts.filter((item) => {
      const matchUnidad = !selectedUnidad || item.unidad === selectedUnidad;

      if (!matchUnidad) return false;
      if (!normalizedQuery) return true;

      const cargoNorm = normalizeText(item.cargo);
      const nombreNorm = normalizeText(item.nombre);
      const unidadNorm = normalizeText(item.unidad);
      const extensionNorm = normalizeText(item.extension);
      const correoNorm = normalizeText(item.correo);

      return (
        cargoNorm.includes(normalizedQuery) ||
        nombreNorm.includes(normalizedQuery) ||
        unidadNorm.includes(normalizedQuery) ||
        extensionNorm.includes(normalizedQuery) ||
        correoNorm.includes(normalizedQuery)
      );
    });
  }, [contacts, searchTerm, selectedUnidad]);

  // Manejo de ESC y bloqueo de scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedUnidad("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div
      className="dt-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dt-modal-title"
    >
      <div className="dt-glass-container">
        {/* ====== Header ====== */}
        <div className="dt-header">
          <div className="dt-header-left">
            <div className="dt-header-icon-box">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
                aria-hidden="true"
              >
                <path d="M19 2H6c-1.2 0-2 .8-2 2v16c0 1.2.8 2 2 2h13c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4zm13 16H6v-2h13v2zm0-4H6v-2h13v2zm0-4h-6V4h6v8z" />
              </svg>
            </div>
            <div className="dt-header-titles">
              <h2 id="dt-modal-title" className="dt-title titdirectoriog">
                Directorio General
              </h2>
              <p className="dt-subtitle">
                Instituto Tlaxcalteca de Elecciones • Tel. (246) 465 03 40
              </p>
            </div>
          </div>

          <div className="dt-header-right">
            <img
              src="https://itetlax.org.mx/assets/img/logoite.webp"
              alt="Logotipo del Instituto Tlaxcalteca de Elecciones"
              className="dt-logo-ite"
            />
            <button
              type="button"
              className="dt-btn-close"
              onClick={onClose}
              title="Cerrar modal"
              aria-label="Cerrar modal de Directorio General"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* ====== Controles de Búsqueda y Filtros ====== */}
        <div className="dt-controls-bar">
          <div className="dt-search-wrapper">
            <span className="dt-search-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="dt-search-input"
              placeholder="Buscar por nombre, cargo, unidad o correo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Buscar en el directorio general"
            />
          </div>

          <div className="dt-filter-wrapper">
            <select
              className="dt-filter-select"
              value={selectedUnidad}
              onChange={(e) => setSelectedUnidad(e.target.value)}
              aria-label="Filtrar por Unidad Administrativa"
            >
              <option value="">Todas las unidades administrativas</option>
              {unidadesList.map((unidad) => (
                <option key={unidad} value={unidad}>
                  {unidad}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ====== Contenido: Data Grid con Glassmorphism ====== */}
        <div className="dt-modal-body">
          {filteredData.length === 0 ? (
            <div className="dt-empty-state">
              <div className="dt-empty-icon" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </div>
              <h3 className="dt-empty-title">Sin resultados coincidentes</h3>
              <p className="dt-empty-text">
                No encontramos registros que coincidan con los criterios de búsqueda.
              </p>
              <button
                type="button"
                className="dt-btn-reset"
                onClick={handleClearFilters}
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <>
              {/* ====== Data Grid (Escritorio / Tablet) ====== */}
              <div className="dt-data-grid">
                {/* Encabezado Grid Minimalista */}
                <div className="dt-grid-header">
                  <div className="dt-header-cell">CLAVE / NIVEL</div>
                  <div className="dt-header-cell">NOMBRE</div>
                  <div className="dt-header-cell">UNIDAD ADMINISTRATIVA</div>
                  <div className="dt-header-cell" style={{ textAlign: "center" }}>EXTENSIÓN</div>
                  <div className="dt-header-cell">CORREO ELECTRÓNICO</div>
                </div>

                {/* Filas con Efecto Vidrio, Elevación y Hover */}
                {filteredData.map((item, index) => (
                  <div className="dt-grid-row" key={`${item.nombre}-${index}`}>
                    <div className="dt-cell">
                      <span className="dt-badge-level">{item.cargo}</span>
                    </div>

                    <div className="dt-cell dt-cell-name">
                      {item.nombre}
                    </div>

                    <div className="dt-cell dt-cell-unit">
                      {item.unidad}
                    </div>

                    <div className="dt-cell" style={{ justifyContent: "center" }}>
                      <a
                        href={`tel:2464650340,${item.extension}`}
                        className="dt-chip-extension"
                        title={`Llamar al conmutador 246 465 03 40 ext. ${item.extension}`}
                        aria-label={`Llamar al conmutador 246 465 03 40 ext. ${item.extension}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55-.45-1-1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                        </svg>
                        {item.extension}
                      </a>
                    </div>

                    <div className="dt-cell">
                      <a
                        href={`mailto:${item.correo}`}
                        className="dt-link-email"
                        title={`Enviar correo electrónico a ${item.nombre}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        {item.correo}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* ====== Tarjetas Móviles Glassmorphism ====== */}
              <div className="dt-mobile-cards">
                {filteredData.map((item, index) => (
                  <div className="dt-glass-card" key={`mobile-card-${item.nombre}-${index}`}>
                    <div className="dt-card-field">
                      <span className="dt-card-label">CLAVE / NIVEL</span>
                      <div>
                        <span className="dt-badge-level">{item.cargo}</span>
                      </div>
                    </div>

                    <div className="dt-card-field">
                      <span className="dt-card-label">NOMBRE</span>
                      <div className="dt-cell-name">{item.nombre}</div>
                    </div>

                    <div className="dt-card-field">
                      <span className="dt-card-label">UNIDAD ADMINISTRATIVA</span>
                      <div className="dt-cell-unit">{item.unidad}</div>
                    </div>

                    <div className="dt-card-field-row">
                      <div className="dt-card-field">
                        <span className="dt-card-label">EXTENSIÓN</span>
                        <div style={{ marginTop: "0.25rem" }}>
                          <a
                            href={`tel:2464650340,${item.extension}`}
                            className="dt-chip-extension"
                            title={`Llamar al conmutador 246 465 03 40 ext. ${item.extension}`}
                            aria-label={`Llamar al conmutador 246 465 03 40 ext. ${item.extension}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55-.45-1-1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                            </svg>
                            {item.extension}
                          </a>
                        </div>
                      </div>

                      <div className="dt-card-field">
                        <span className="dt-card-label">CORREO</span>
                        <div style={{ marginTop: "0.25rem" }}>
                          <a
                            href={`mailto:${item.correo}`}
                            className="dt-link-email"
                            title={`Enviar correo electrónico a ${item.nombre}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                              <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                            {item.correo}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ====== Footer ====== */}
        <div className="dt-footer">
          <button
            type="button"
            className="dt-btn-pdf"
            onClick={handleGenerarYAbrirPDF}
            disabled={isGeneratingPDF}
            aria-label="Descargar Directorio en formato PDF"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <polyline points="9 15 12 18 15 15"></polyline>
            </svg>
            {isGeneratingPDF ? "Generando..." : "Directorio PDF"}
          </button>

          <button
            type="button"
            className="dt-btn-close-main"
            onClick={onClose}
            aria-label="Cerrar modal de Directorio General"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}